import { FC, useCallback, useMemo, useState } from "react";
import { useEffectOnceWhen, useToggle } from "rooks";
import { Box, Collapse, Divider, Grid } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useAppSelector } from "@/store/hooks";
import { CsvDownloadDialogColumnItem } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/CsvDownloadDialogColumnItem";
import { CsvSelectedPossibleColumns } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/utils/types";
import { CsvDownloadDialogColumnsHeader } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/CsvDownloadDialogColumnsHeader";
import { EmptySearchResult } from "@/components/common/EmptySearchResult";
import { billingExplorerCsvPossibleColumnsFilteredFormatedDataSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/csv/possible-columns/billingExplorerCsvPossibleColumnsFilteredFormatedDataSelector";
import { billingExplorerFilterTreeProvidersSelector } from "@/store/resource-explorer/selectors/filter-tree/billingExplorerFilterTreeProvidersSelector";
import { CsvDownloadDialogToolbar } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/CsvDownloadDialogToolbar";
import { CsvDownloadDialogDimensions } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/dimensions/CsvDownloadDialogDimensions";
import { CsvDownloadDialogTagKeys } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/tag-keys/CsvDownloadDialogTagKeys";
import { BillingExplorerCSVColumnItem } from "@/store/resource-explorer/selectors/current-resource-explorer/csv/possible-columns/utils/types";
import { sortColumnsBySelectedState } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/utils/sortColumnsBySelectedState";

interface CsvDownloadDialogColumnsProps {
  selectedColumns: CsvSelectedPossibleColumns | null;
  onSelectedColumnsChange(data: CsvSelectedPossibleColumns): void;
  dimensions: string[];
  onDimensionChange(data: string[]): void;
  tagKeys: string[];
  onTagKeyChange(data: string[]): void;
}

export const CsvDownloadDialogColumns: FC<CsvDownloadDialogColumnsProps> = ({
  selectedColumns,
  onSelectedColumnsChange,
  dimensions,
  onDimensionChange,
  tagKeys,
  onTagKeyChange,
}) => {
  const [collapsed, toggleCollapsed] = useToggle(true);
  const [search, setSearch] = useState("");
  const [initialSortedData, setInitialSortedData] = useState<
    BillingExplorerCSVColumnItem[] | undefined
  >();
  const data = useAppSelector(
    billingExplorerCsvPossibleColumnsFilteredFormatedDataSelector,
  );
  const filterTreeProviders = useAppSelector(
    billingExplorerFilterTreeProvidersSelector,
  );

  const searchChangeHandler = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const filteredData = useMemo(() => {
    const source = initialSortedData ?? data;
    if (!source) return;

    if (!search?.length) return source;

    const searchTerm = search.toLowerCase().trim();
    return source.filter((item) =>
      item.label.toLowerCase().trim().includes(searchTerm),
    );
  }, [search, initialSortedData, data]);

  const selectedLength = useMemo(() => {
    return filteredData?.filter((item) => selectedColumns?.[item.keyValue])
      ?.length;
  }, [selectedColumns, filteredData]);

  const isAllSelected = selectedLength === filteredData?.length;
  const isSomeSelected =
    !!selectedLength && selectedLength !== filteredData?.length;

  const unselectClickHandler = useCallback(() => {
    const selectedItems =
      filteredData?.reduce((result, item) => {
        result = {
          ...result,
          [item.keyValue]: item.disabled || !isAllSelected,
        };
        return result;
      }, {} as CsvSelectedPossibleColumns) ?? {};
    onSelectedColumnsChange(selectedItems);
  }, [isAllSelected, filteredData, onSelectedColumnsChange]);

  useEffectOnceWhen(() => {
    if (!data || initialSortedData || !selectedColumns) return;

    const sorted = sortColumnsBySelectedState(data, selectedColumns);

    setInitialSortedData(sorted);
  }, !!data && !!selectedColumns);

  return (
    <Stack
      border={1}
      flexGrow={1}
      borderRadius={2}
      overflow="hidden"
      borderColor="grey.300"
    >
      <CsvDownloadDialogColumnsHeader
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />
      {collapsed && <Divider />}

      <Collapse in={collapsed}>
        <CsvDownloadDialogToolbar
          search={search}
          isAllSelected={isAllSelected}
          isSomeSelected={isSomeSelected}
          onSearchChange={searchChangeHandler}
          onUnselectAllClickHandler={unselectClickHandler}
        />

        <Stack direction="row" spacing={2} px={2} pt={2}>
          <CsvDownloadDialogTagKeys
            tagKeys={tagKeys}
            onTagKeyChange={onTagKeyChange}
          />
          <CsvDownloadDialogDimensions
            dimensions={dimensions}
            onDimensionChange={onDimensionChange}
          />
        </Stack>

        <Box maxHeight="50dvh" overflow="auto">
          <Grid
            mt={0}
            container
            px={1.5}
            pb={1.5}
            spacing={2}
            columnSpacing={2}
          >
            {filteredData && filteredData.length === 0 ? (
              <Grid item xs={12} my={7}>
                <EmptySearchResult />
              </Grid>
            ) : (
              filteredData?.map((item) => {
                const showProviders =
                  !filterTreeProviders?.length ||
                  filterTreeProviders?.includes("dimensions") ||
                  ((filterTreeProviders?.length ?? 0) !==
                    item.providers?.length &&
                    (filterTreeProviders?.length ?? 0) > 1);
                return (
                  <CsvDownloadDialogColumnItem
                    key={item.label}
                    disabled={item.disabled}
                    label={item.label}
                    providers={item.providers}
                    keyValue={item.keyValue}
                    checked={!!selectedColumns?.[item.keyValue]}
                    onSelectedColumnsChange={onSelectedColumnsChange}
                    showProviders={showProviders}
                  />
                );
              })
            )}
          </Grid>
        </Box>
      </Collapse>
    </Stack>
  );
};
